import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { getNewsRoomData, getSingleSegmentData, resetNewsRoomData } from '../../containers/newsroomSlice/newsRoomSlice'
import { useNavigate, useSearchParams } from 'react-router-dom';
import ImageCards from '../../components/ImageCards/ImageCards';
import { useTranslation } from 'react-i18next';
import { getCurrentLocal } from '../../utils';
import { resultPerPage } from '../../constants/paginationConstants';
 
const NewsRoom = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const seeAllToken = {
    label: t('seeAll'),
    title: "See All",
    id: "see-all"
  };
  const pressReleaseToken = {
    label: t('pressRelease'),
    title: "Press Release",
    id: "press-release"
  };
  const mediaKitsToken = {
    label: t('mediaKits'),
    title: "Media Kits",
    id: "media-kits"
  };
  const eventsToken = {
    label: t('events'),
    title: "Events",
    id: "events"
  };
  const blogsToken = {
    label: t('blogs'),
    title: "Blogs",
    id: "blogs"
  };
  const mediaInquiriesToken = {
    label: t('mediaInquiries'),
    title: "Media Inquiries",
    id: "media-inquiries"
  };
  const navigate = useNavigate();
  const newsRoomApiResponse = useSelector((state) => state?.newsRoomDetails);
  const isApiLoading = useSelector((state) => state?.newsRoomDetails?.loading);
  const [facetResults, setFacetResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  let ignore = false;
  const [filters, setFilters] = useState({})

  const [totalCount, setTotalCount] = useState(0);
  const [newsRoomData, setNewsRoomData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState("See All")
  
  const getlocales = window.location.href
  const matchLocal = getlocales.match(/\/([a-z]{2}-[a-z]{2})\//i);

  useEffect(() => {
    setNewsRoomData([]);
    resetNewsRoomData();
    if (!ignore) {
      const page_type = searchParams.get('page-type');
      const page = parseInt(searchParams.get('p')) || 1;
      setCurrentPage(page);
      if (page_type === null && page === 1) {
        const newsRoomQuery = getSeeAllData([pressReleaseToken.title, blogsToken.title, eventsToken.title, mediaKitsToken.title]);
        if (newsRoomQuery) {
          // dispatch(getNewsRoomData(newsRoomQuery));
        }
      } else {
        const newsRoomFilterQuery = getSingleSegmentQuery(page_type, page);
        if (newsRoomFilterQuery) {
          dispatch(getSingleSegmentData(newsRoomFilterQuery));
        }
        setSelectedFilters(page_type);
      }
    }
    return () => {
      ignore = true;
    };
  }, [searchParams]);

  useEffect(() => {
    if (newsRoomApiResponse.newsData) {
      const finalNewsroomData = prepareNewsRoomData(newsRoomApiResponse.newsData);
      setNewsRoomData(finalNewsroomData);
      setTotalCount(newsRoomApiResponse?.total || 0);
      setTotalPages(Math.ceil(newsRoomApiResponse?.total / resultPerPage));
    }
  }, [newsRoomApiResponse]);

  useEffect(() => {
    if (selectedFilters === "See All") {
      setPages([]);
      setTotalPages(1);
      setCurrentPage(1);
      return;
    }

    const maxPagesToShow = 5;
    const pagesCount = totalPages;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagesCount, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const newPages = [];
    for (let i = startPage; i <= endPage; i++) {
      newPages.push(
        <li key={i}>
          <button
            className={`sd-py-1 sd-px-2 ${currentPage === i ? 'sd-bg-black sd-text-white' : 'sd-bg-white sd-text-black'} sd-rounded-sm hover:sd-bg-black hover:sd-text-white ${pagesCount === 1 ? 'sd-mx-5' : ''}`}
            onClick={() => handlePageClick(i)}
          >
            {i}
          </button>
        </li>
      );
    }
    if (endPage < pagesCount) {
      newPages.push(
        <li key="ellipsis">
          <button className="sd-py-1 sd-px-2 sd-text-gray-300 sd-rounded-sm hover:sd-bg-black hover:sd-text-white">
            ...
          </button>
        </li>
      );
    }
    setPages(newPages);
  }, [currentPage, totalPages, selectedFilters]);

  const prepareNewsRoomData = (data) => {
    let flattenedArray = [];
    if (selectedFilters === "See All") {
      const Objkeys = Object.keys(data);
      const sortedArray = [];
      const order = ['Press Release', 'Blogs', 'Events', 'Media Kits'];
      order.forEach(type => {
        if (data[type]) {
          const sortedItems = [...data[type]] // Create a copy of the array
            .sort((a, b) => new Date(b.documentTypeDate) - new Date(a.documentTypeDate))
            .slice(0, 5);
          sortedArray.push(...sortedItems);
        }
      });
      flattenedArray = sortedArray;
      if (flattenedArray.length > 20) {
        flattenedArray.splice(20);
      }
    } else {
      flattenedArray = data;
    }
    return flattenedArray;
  };

  const handlePageClick = (pageNumber) => {
    // Scroll to the top of the image card container
    const imageCardContainer = document.querySelector(".sd-image-card-container");
    if (imageCardContainer) {
      imageCardContainer.scrollIntoView({ behavior: "smooth" });
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('p', pageNumber.toString());
    setCurrentPage(pageNumber);
    navigate(`?${newSearchParams.toString()}`);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      handlePageClick(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageClick(currentPage + 1);
    }
  };

  const selectFilter = (filter) => {
    setNewsRoomData([]);
    let newSearchParams = new URLSearchParams(searchParams);
    if (newSearchParams.toString().length > 1) {
      newSearchParams.delete('p');
      navigate(`?${newSearchParams.toString()}`);
    } else {
      newSearchParams.set('p', '1');
      navigate(`?${newSearchParams.toString()}`);
    }
  };

  const handlePagetType = (e, item) => {
    dispatch(resetNewsRoomData());
    setSelectedFilters(item);
    const newSearchParams = new URLSearchParams(searchParams);
    if (item !== 'See All') {
      newSearchParams.set('p', '1');
      newSearchParams.set('page-type', item);
      navigate(`?${newSearchParams.toString()}`);
      trackPageViewsOnReact(getlocales, item);
    } else {
      newSearchParams.delete('page-type');
      newSearchParams.delete('p');
      navigate(`?${newSearchParams.toString()}`);
    }
  };

   const handleMediaInquiries = () => {
    window.location.href = 'mailto:mediainquiries@sandisk.com'; 
  };
  
const trackPageViewsOnReact = (pageURL, buttonName) => {
    let utag_data = window.utag_data;
    const pathName = window.location.pathname;
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const pageType = urlParams.get('page-type');
    if (utag_data && utag_data['contentEngaged']) {
      delete utag_data['contentEngaged'];
    }
    if (utag_data && utag_data['linkDestination']) {
      delete utag_data['linkDestination'];
    }
    if (utag_data && utag_data['linkHeadline']) {
      delete utag_data['linkHeadline'];
    }
    if (pageType) {
      window.utag_data = {
        ...window.utag_data,
        pageName: !matchLocal ? 'en-us' + pathName + '/' + pageType.toLowerCase().replace(' ', '-') : (pathName + '/' + pageType.toLowerCase().replace(' ', '-')).slice(1)
      };
    }
    var PageViews = {};
    PageViews["pageURL"] = window.location.href;
    PageViews["buttonName"] = buttonName;
    try {
      var event = window.aa.createEvent("page_view", PageViews);
      window.dispatchEvent(event);
    } catch (e) {
      console.log("Error while triggering analytics Page Views event.");
    }
  };

  const getNewsRoomQuery = (filter) => {
    const selectColumns = "title,url,description,primaryImage,documentType,documentTypeDate";
    const searchQuery = "*";
    const sortDateParams = "&startDate=2024-01-01T00:00:00Z&endDate=2024-12-29T23:59:00Z";
    const sortParams = "&sort=documentTypeDate" + sortDateParams;
    const rowsParams = "&rows=20";
    const groupLimit = "&groupLimit=5";
    const groupBy = "&groupBy=documentType&sort=documentTypeDate";
    let filterParams = "&fq=documentType:Press%20Release,Events,Blogs,Media%20Kits";   
    let finalProductQuery;
    if (selectedFilters?.toLowerCase() === 'press release') {
      finalProductQuery = "?q=" + searchQuery + "&locale=" + getCurrentLocal().toLowerCase() + "&fl=" + selectColumns + filterParams + groupLimit;
    } else {
      finalProductQuery = "?q=" + searchQuery + "&locale=" + getCurrentLocal().toLowerCase() + "&fl=" + selectColumns + filterParams + groupLimit + groupBy;
    }
    return finalProductQuery;
    
  };

  const getSeeAllData = async (filterArray) => {
    let tempData = [];
    for (const pageTitle of filterArray) {
    // filterArray.forEach(async (pageTitle) => {
      const searchQuery = '*';
      const selectColumns = 'title,url,description,primaryImage,documentType,documentTypeDate';
      let filterParams = '&fq=documentType:' + pageTitle;
      const groupBy = '&groupBy=documentType';
      const sortParams = '&sort=documentTypeDate&startDate=2018-01-01T00:00:00Z&endDate=2024-12-29T23:59:00Z';
      const paginationParams = "&from=0";
      const groupLimit = "&rows=5&sort=documentTypeDate";
      let finalProductQuery;
      if (pageTitle?.toLowerCase() === 'press release') {
        finalProductQuery = "?q=" + searchQuery + "&locale=" + getCurrentLocal().toLowerCase() + "&fl=" + selectColumns + filterParams + paginationParams + groupLimit;
      } else {
        finalProductQuery = "?q=" + searchQuery + "&locale=" + getCurrentLocal().toLowerCase() + "&fl=" + selectColumns + filterParams + groupBy + paginationParams + groupLimit;
      }
      await dispatch(getSingleSegmentData(finalProductQuery)).then(async (res) => {
        if(res.payload?.docs){
          tempData = [...tempData , ...res?.payload?.docs]
        }
      })
      // console.log(fetchedData);
      setNewsRoomData(tempData);
      console.log(tempData);
    }
  };

  const getSingleSegmentQuery = (filter, page) => {
    const searchQuery = '*';
    const selectColumns = 'title,url,description,primaryImage,documentType,documentTypeDate';
    let filterParams = '&fq=documentType:' + filter;
    const groupBy = '&groupBy=documentType';
    const sortParams = '&sort=documentTypeDate&startDate=2018-01-01T00:00:00Z&endDate=2024-12-29T23:59:00Z';
    const paginationParams = "&from=" + ((page - 1) * resultPerPage);
    const groupLimit = `&rows=${resultPerPage}&sort=documentTypeDate`;
    let finalProductQuery;
    if (selectedFilters?.toLowerCase() === 'press release' || filter?.toLowerCase() === 'press release') {
      finalProductQuery = "?q=" + searchQuery + "&locale=" + getCurrentLocal().toLowerCase() + "&fl=" + selectColumns + filterParams + paginationParams + groupLimit;
    } else {
      finalProductQuery = "?q=" + searchQuery + "&locale=" + getCurrentLocal().toLowerCase() + "&fl=" + selectColumns + filterParams + groupBy + paginationParams + groupLimit;
    }
    return finalProductQuery;
  };

  return (
    <section className="sd-relative">
      <div className="sd-press-release-container sd-container lg:sd-mt-[174px] sd-mt-[80px]">
        <div className="">
          <div className="sd-grid">
            <div>
              <h1 className="sd-uppercase sd-text-[13px] sd-titleText sd-mb-[14px] sd-mb-[8px]">
                {t('newsroom')}
              </h1>
            </div>
            <div className="sd-grid lg:sd-grid-cols-16 sd-grid-cols-8">
              <div className='lg:sd-col-span-8 sd-col-span-8 sd-mb-[24px]'>
                <p className="lg:sd-text-[56px] sd-text-[23px] sd-font-pilat sd-leading-[100%] lg:sd--tracking-[1.12px] sd--tracking-[letter-spacing:0.23px] lg:sd-col-span-8 sd-col-span-8 sd-mb-[24px]">{t('description')}</p>
                 <button
                  onClick={handleMediaInquiries}
                  className="sd-sm-btn-grey active:sd-bg-black active:sd-text-white sd-whitespace-nowrap sd-bg-[#E10600] hover:sd-bg-[#D21920] active:bg-[#D21920] active:shadow-[2px_2px_0_0_rgba(0,0,0,0.23)] sd-text-white"
                >
                  {mediaInquiriesToken.label}
                </button>
              </div>
              <div className="lg:sd-col-span-7 lg:sd-col-start-10 sd-col-span-8 lg:sd-text-left lg:sd-content-end lg:sd-justify-self-end sd-text-center sd-flex lg: sd-overflow-auto sd-scroll-hidden sd-mb-[24px] sm:sd-justify-end sd-items-end">
                {[seeAllToken, pressReleaseToken, mediaKitsToken, eventsToken, blogsToken].map((item) => (
                  <button
                    onClick={(e) => handlePagetType(e, item.title)}
                    key={item.id}
                    className={`sd-sm-btn-grey active:sd-bg-black active:sd-text-white sd-whitespace-nowrap ${selectedFilters === item.title ? "sd-bg-black sd-text-white sd-pointer-events-none" : ""}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {!isApiLoading ? (
            <h3 className={newsRoomData?.length > 0 ? 'sd-hidden' : 'sd-mt-[88px] lg:sd-mt-[137px] sd-text-center'}>{t('noRecordFound')}</h3>
          ) : null}
          <div className='sd-image-card-container sd-mb-[3rem]'>
            {<ImageCards data={newsRoomData} totalCount={totalCount} isApiLoading={isApiLoading} pageType={searchParams.get('page-type')} />}
          </div>
          {newsRoomData && newsRoomData.length > 0 && selectedFilters !== "See All" && (
            <div className="sd-pagination-container sd-flex sd-items-baseline sd-justify-center sd-py-4">
              <button
                disabled={currentPage === 1}
                className={`sd-py-1 sd-px-2 ${currentPage === 1 ? 'sd-pointer-events-none sd-text-gray-300' : 'sd-text-black'} sd-bg-white sd-rounded-sm active:sd-bg-black active:sd-text-white`}
                onClick={handlePrev}
              >
                {t('previous')}
              </button>
              <ul className="sd-flex sd-space-x-3 sd-items-center">
                {pages}
              </ul>
              <button
                disabled={currentPage === totalPages}
                className={`sd-py-1 sd-px-2 ${currentPage === totalPages ? 'sd-pointer-events-none sd-text-gray-300' : ''} sd-bg-white sd-text-black sd-rounded-sm hover:sd-bg-black hover:sd-text-white`}
                onClick={handleNext}
              >
                {t('next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsRoom;